import { api } from "./client";
import type { ProvenancePayload } from "../provenance/buildProvenance";

export type WorkRow = {
  id: string;
  created_at: string | null;
  title: string;
  type: string;
  format: string;
  status: string | null;
  profiles: string[];
};

export type WorkSortField = "created_at" | "title" | "type" | "format";
export type SortDir = "asc" | "desc";

export type WorkListResponse = {
  works: WorkRow[];
  page: number;
  page_size: number;
  total: number;
  sort?: WorkSortField;
  dir?: SortDir;
};

export type WorkListParams = {
  page: number;
  page_size?: number;
  type?: string;
  format?: string;
  status?: string;
  profile?: string;
  created_after?: string;
  created_before?: string;
  expr?: string;
  sort?: WorkSortField;
  dir?: SortDir;
};

export type WorkStats = {
  by_status: Record<string, number>;
  by_type_approved: Record<string, number>;
  added: { day: number; week: number; month: number };
};

export type WorkCertificationEvidence = {
  id: string;
  type_name: string | null;
  supporting_data: string | null;
  comment: string | null;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string | null;
};

export type WorkCertification = {
  id: string;
  status: string | null;
  type: { id: string; name: string; version: number } | null;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string | null;
  cert_signature_b64: string | null;
  external_id: string | null;
  evidence: WorkCertificationEvidence[];
};

export type WorkDetail = {
  id: string;
  created_at: string | null;
  updated_at: string | null;
  title: string;
  type: string;
  format: string;
  description: string | null;
  url: string | null;
  img_asset: string | null;
  img_asset_url: string | null;
  profile: { id: string; name: string } | null;
  creator: {
    id: string;
    name: string | null;
    email: string | null;
    status: string;
  } | null;
  certifications: WorkCertification[];
};

export const WorksApi = {
  list(params: WorkListParams) {
    return api
      .get<WorkListResponse>("/works", {
        params: {
          page: params.page,
          page_size: params.page_size ?? 40,
          type: params.type || undefined,
          format: params.format || undefined,
          status: params.status || undefined,
          profile: params.profile || undefined,
          created_after: params.created_after || undefined,
          created_before: params.created_before || undefined,
          expr: params.expr || undefined,
          sort: params.sort || undefined,
          dir: params.dir || undefined,
        },
      })
      .then((r) => r.data);
  },
  types() {
    return api.get<{ types: string[] }>("/works/types").then((r) => r.data.types);
  },
  formats() {
    return api.get<{ formats: string[] }>("/works/formats").then((r) => r.data.formats);
  },
  stats() {
    return api.get<WorkStats>("/works/stats").then((r) => r.data);
  },
  get(id: string) {
    return api.get<WorkDetail>(`/works/${id}`).then((r) => r.data);
  },
  update(id: string, patch: WorkPatch, audit: { reason: string }) {
    return api
      .patch<WorkDetail>(`/works/${id}`, { ...patch, ...audit })
      .then((r) => r.data);
  },
  bulkDelete(ids: string[]) {
    return api
      .delete<{ deleted: string[]; refused: string[] }>("/works", {
        data: { ids },
      })
      .then((r) => r.data);
  },
  create(body: WorkCreate) {
    return api.post<WorkDetail>("/works", body).then((r) => r.data);
  },
  imageUploadUrl(filename: string, contentType: string) {
    return api
      .post<{ uploadUrl: string; key: string }>("/works/image-upload-url", {
        filename,
        content_type: contentType,
      })
      .then((r) => r.data);
  },
  bulkImport(body: { csv: string }) {
    return api
      .post<WorkBulkImportResult>("/works/bulk-import", body)
      .then((r) => r.data);
  },
  bulkUpdate(ids: string[], changes: WorkBulkChanges, audit: { reason: string }) {
    return api
      .patch<{ updated: string[]; refused: string[]; no_certification?: string[] }>(
        "/works",
        { ids, changes, reason: audit.reason },
      )
      .then((r) => r.data);
  },
};

// Canonical worktypes (mirrors database-orm orm.WORK_TYPES).
export const WORK_TYPES = [
  "AUDIO",
  "DIGITAL_ART",
  "OTHER",
  "PHOTOGRAPH",
  "VIDEO",
  "WRITTEN_WORD",
] as const;

// Certification statuses a support agent may bulk-apply (the manual-override set).
export const WORK_BULK_STATUSES = ["APPROVED", "REJECTED"] as const;

export type WorkBulkChanges = {
  type?: string;
  format?: string;
  creator_profile_id?: string;
  status?: (typeof WORK_BULK_STATUSES)[number];
};

export type WorkBulkImportResult = {
  imported: { row: number; work_id: string }[];
  skipped: { row: number; reason: string }[];
  errors: { row: number; reason: string; fields?: string[]; value?: string }[];
  failed_images: { row: number; image_url: string; reason: string }[];
};

/** Downscale to a max 600px-tall JPEG and PUT it to S3 via a presigned URL.
 *  Returns the work-images/<uuid>.jpg key to store as img_asset. Mirrors the
 *  customer-app cover-art upload. */
export async function uploadWorkImage(file: File): Promise<string> {
  const resized = await resizeImageTo600pxJpeg(file);
  const { uploadUrl, key } = await WorksApi.imageUploadUrl(resized.name, resized.type);
  const put = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": resized.type },
    body: resized,
  });
  if (!put.ok) throw new Error(`Upload failed (${put.status})`);
  return key;
}

const COVER_TARGET_HEIGHT_PX = 600;

async function resizeImageTo600pxJpeg(source: File): Promise<File> {
  const bitmap = await createImageBitmap(source);
  try {
    const scale = bitmap.height > COVER_TARGET_HEIGHT_PX ? COVER_TARGET_HEIGHT_PX / bitmap.height : 1;
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D context unavailable");
    ctx.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.85),
    );
    if (!blob) throw new Error("Image encoding failed");
    return new File([blob], "cover.jpg", { type: "image/jpeg" });
  } finally {
    bitmap.close?.();
  }
}

export type WorkCreate = {
  creator_profile_id: string;
  title: string;
  type: string;
  format: string;
  description?: string | null;
  url?: string | null;
  img_asset_key?: string | null;
  certification?: {
    type_id: string;
    evidence?: string | null;
    evidence_comment?: string | null;
    approved_by?: string | null;
  };
  provenance?: ProvenancePayload;
};

export type WorkPatch = {
  title?: string;
  type?: string;
  format?: string;
  description?: string | null;
  url?: string | null;
  img_asset?: string | null;
};

export type CertType = { id: string; name: string; version: number };

export const CertificationsApi = {
  types() {
    return api
      .get<{ types: CertType[] }>("/certification-types")
      .then((r) => r.data.types);
  },
  manualOverride(
    certificationId: string,
    status: "approved" | "rejected",
    reason: string,
    provenance?: ProvenancePayload,
  ) {
    return api
      .post<{
        certification_id: string;
        status: string;
        approved_by: string | null;
        approved_at: string | null;
        evidence_id: string;
        asset_generation_queued: boolean;
      }>(`/certifications/${certificationId}/manual-override`, {
        status,
        reason,
        ...(provenance ? { provenance } : {}),
      })
      .then((r) => r.data);
  },
  regenerateSignature(certificationId: string) {
    return api
      .post<{ certification_id: string; asset_generation_queued: boolean }>(
        `/certifications/${certificationId}/regenerate-signature`,
      )
      .then((r) => r.data);
  },
};

